import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { staffApi, setStaffToken, setStaffCtx, getStaffToken } from "../../lib/staff";
import { formatErr } from "../../lib/api";
import { ScanLine, Loader2, MapPin, User, KeyRound, Smartphone } from "lucide-react";

export default function StaffLoginPage() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const venueParam = (sp.get("venue") || "").trim().toLowerCase();
  const [venue, setVenue] = useState(venueParam);
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [device, setDevice] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (getStaffToken()) nav("/app", { replace: true });
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!venue || !name.trim() || !pin || !device.trim()) {
      toast.error("Fill in every field to sign in.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await staffApi.post("/staff/login", {
        venue: venue.trim().toLowerCase(),
        name: name.trim(),
        pin: pin.trim(),
        device_nickname: device.trim(),
      });
      setStaffToken(data.token);
      setStaffCtx({ staff: data.staff, attraction: data.attraction });
      toast.success(`Welcome, ${data.staff.name}`);
      nav("/app", { replace: true });
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Could not sign in.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col items-center justify-center px-6" data-testid="staff-login-page">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center gap-2 mb-8">
          <div className="w-14 h-14 rounded-2xl bg-[#dc2626]/15 border border-[#dc2626]/40 flex items-center justify-center">
            <ScanLine className="text-[#dc2626]" size={26} />
          </div>
          <div className="st-brand text-2xl mt-1">SCARE<span className="text-[#dc2626]">TICKETS</span></div>
          <div className="text-sm text-[#8a8a8a]">Gate check-in — staff sign in</div>
        </div>

        <form onSubmit={submit} className="space-y-3" data-testid="staff-login-form">
          <Field icon={MapPin} label="Venue">
            <input
              className="st-input w-full"
              placeholder="venue code (e.g. nightmare-manor)"
              value={venue}
              onChange={(e) => setVenue(e.target.value)}
              readOnly={!!venueParam}
              autoCapitalize="off"
              data-testid="staff-venue-input"
            />
          </Field>
          <Field icon={User} label="Your name">
            <input
              className="st-input w-full"
              placeholder="e.g. Gary"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="staff-name-input"
            />
          </Field>
          <Field icon={KeyRound} label="PIN">
            <input
              className="st-input w-full tracking-[0.4em] text-lg"
              placeholder="••••"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              autoComplete="off"
              type="password"
              data-testid="staff-pin-input"
            />
          </Field>
          <Field icon={Smartphone} label="Device name">
            <input
              className="st-input w-full"
              placeholder="e.g. Kiosk 1"
              value={device}
              onChange={(e) => setDevice(e.target.value)}
              data-testid="staff-device-input"
            />
          </Field>

          <button type="submit" className="st-btn w-full justify-center py-3 mt-2" disabled={busy} data-testid="staff-login-submit">
            {busy ? <Loader2 className="animate-spin" size={18} /> : <ScanLine size={18} />}
            {busy ? "Signing in…" : "Start check-in"}
          </button>
        </form>

        <p className="text-xs text-[#6a6a6a] text-center mt-6">
          Ask your manager for your name, PIN and venue code.
        </p>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <label className="block">
      <div className="flex items-center gap-1.5 text-xs text-[#8a8a8a] mb-1.5">
        <Icon size={13} /> {label}
      </div>
      {children}
    </label>
  );
}
